/**
 * Pure helpers shared by the Publish dialog (chat) and the Published Reports
 * dashboard plugin: visibility / permission rules, deep links, provenance
 * parsing and the title default (publish-report spec, Req 2.2, 5.6, 6.1).
 *
 * These mirror the server-side rules in the publishReport / unpublishReport
 * resolvers (SponsoredProductsSPFInfraCommon). The server stays authoritative:
 * the UI only hides controls the server would reject anyway.
 */

import type {
  PublishedReport,
  PublishedReportSource,
  PublishedReportSummary,
} from "../../models/API";

/** The FINN team, which alone manages Global resources. */
export const FINN_TEAM_ID = 1;

export const PUBLISHED_REPORTS_DASHBOARD_ID = "published-reports";

export type ReportVisibility = "private" | "public" | "global";

export const TITLE_MAX_LENGTH = 200;
export const TITLE_DEFAULT_LENGTH = 120;

export function isOperatorOrAdmin(effectiveRole: string | undefined): boolean {
  return effectiveRole === "admin" || effectiveRole === "operator";
}

/**
 * Which visibilities the actor may SET for a report in `teamId` (Req 2.2):
 * private for everyone; public for operators/admins; global only for
 * operators/admins publishing on the FINN team.
 */
export function allowedVisibilities(
  effectiveRole: string | undefined,
  teamId: number | undefined
): ReportVisibility[] {
  const allowed: ReportVisibility[] = ["private"];
  if (isOperatorOrAdmin(effectiveRole)) {
    allowed.push("public");
    if (teamId === FINN_TEAM_ID) allowed.push("global");
  }
  return allowed;
}

export function visibilityOf(report: {
  is_public: boolean;
  is_global: boolean;
}): ReportVisibility {
  if (report.is_global) return "global";
  if (report.is_public) return "public";
  return "private";
}

export function visibilityFlags(visibility: ReportVisibility): {
  is_public: boolean;
  is_global: boolean;
} {
  return {
    is_public: visibility === "public" || visibility === "global",
    is_global: visibility === "global",
  };
}

export const VISIBILITY_LABELS: Record<ReportVisibility, string> = {
  private: "Private (only you can see)",
  public: "Public (visible to team)",
  global: "Global (visible to all teams)",
};

/** Knowledge-card colours (KnowledgePage.getCardStyle) for the visibility stripe. */
export const VISIBILITY_STYLE: Record<
  ReportVisibility,
  { backgroundColor: string; borderColor: string; chipLabel: string }
> = {
  private: {
    backgroundColor: "rgba(21, 101, 192, 0.03)",
    borderColor: "#1565c0",
    chipLabel: "Private",
  },
  public: {
    backgroundColor: "rgba(46, 125, 50, 0.03)",
    borderColor: "#2e7d32",
    chipLabel: "Public",
  },
  global: {
    backgroundColor: "rgba(255, 152, 0, 0.03)",
    borderColor: "#ff9800",
    chipLabel: "Global",
  },
};

export interface ManageContext {
  userId: number | undefined;
  /** The actor's effective role in the CURRENT team. */
  effectiveRole: string | undefined;
  currentTeamId: number | undefined;
}

/**
 * Whether the actor may edit / unpublish `report` (Req 6.1, mirrors the SQL
 * manage predicate): its creator; or, for a team-public report of the current
 * team, an operator/admin of that team; or, for a global report, an
 * operator/admin acting on the FINN team.
 *
 * The UI only knows the actor's role in the current team, so a team-public
 * report of ANOTHER team is manageable here only by its creator (the server
 * would allow that team's operators too, but they manage it from that team).
 */
export function canManagePublishedReport(
  report: Pick<
    PublishedReport,
    "creator_id" | "team_id" | "is_public" | "is_global"
  >,
  ctx: ManageContext
): boolean {
  if (ctx.userId !== undefined && report.creator_id === ctx.userId) return true;
  if (!isOperatorOrAdmin(ctx.effectiveRole)) return false;
  if (report.is_global) return ctx.currentTeamId === FINN_TEAM_ID;
  if (report.is_public) return report.team_id === ctx.currentTeamId;
  return false;
}

/** Default title: the question, trimmed to TITLE_DEFAULT_LENGTH characters. */
export function defaultTitleFor(question: string): string {
  const oneLine = (question ?? "").replace(/\s+/g, " ").trim();
  if (oneLine.length <= TITLE_DEFAULT_LENGTH) return oneLine;
  return oneLine.slice(0, TITLE_DEFAULT_LENGTH - 1).trimEnd() + "…";
}

/** Null when valid, otherwise the inline validation message (Req 2.4). */
export function validateTitle(title: string): string | null {
  const trimmed = title.trim();
  if (trimmed.length === 0) return "Title is required";
  if (trimmed.length > TITLE_MAX_LENGTH)
    return `Title must be ${TITLE_MAX_LENGTH} characters or less`;
  return null;
}

/**
 * Deep link to one report (Req 5.6, 5.7). Carries `team` so a reader who is a
 * member of the report's team is switched into it by MainPage's `?team=`
 * handling; readers outside it see the "not available" notice.
 */
export function publishedReportDeepLink(
  report: Pick<PublishedReportSummary, "id" | "team_name">,
  origin: string = window.location.origin
): string {
  const params = new URLSearchParams();
  params.set("view", "dashboard");
  params.set("dashboard", PUBLISHED_REPORTS_DASHBOARD_ID);
  params.set("report", String(report.id));
  if (report.team_name) params.set("team", report.team_name);
  return `${origin}/?${params.toString()}`;
}

/** Parse PublishedReport.source (JSON text, possibly null/malformed) defensively. */
export function parsePublishedReportSource(
  raw: string | null | undefined
): PublishedReportSource {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Decode the hex-encoded `request_scope` stamp of a stored turn (Question.request_scope)
 * into an object, or undefined when absent or malformed (Req 4.2).
 */
export function decodeRequestScope(
  hexToString: (hex: string) => string,
  raw: string | null | undefined
): PublishedReportSource["request_scope"] | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(hexToString(raw));
    return parsed && typeof parsed === "object" ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/** Distinct tool names from a turn's TOOLCALLS marker payload, in first-use order. */
export function toolNamesOf(toolCalls: any[] | undefined): string[] {
  const names: string[] = [];
  (toolCalls ?? []).forEach((call) => {
    const name =
      typeof call?.name === "string"
        ? call.name
        : typeof call?.tool === "string"
        ? call.tool
        : typeof call?.tool_name === "string"
        ? call.tool_name
        : null;
    if (name && !names.includes(name)) names.push(name);
  });
  return names;
}

/** Build the provenance JSON stored with a new report (design §Provenance JSON). */
export function buildProvenance(args: {
  requestScope?: PublishedReportSource["request_scope"];
  toolCalls?: any[];
  sources?: any[];
}): PublishedReportSource {
  return {
    request_scope: args.requestScope ?? null,
    tools_used: toolNamesOf(args.toolCalls),
    sources_count: args.sources?.length ?? 0,
    answered_at: Math.floor(Date.now() / 1000),
  };
}

/** Short human label for a Bedrock model id, e.g. "us.anthropic.claude-opus-4-7..." -> "claude-opus-4-7". */
export function modelLabel(modelId: string | null | undefined): string {
  if (!modelId) return "unknown";
  // e.g. "us.anthropic.claude-opus-4-7-20260101-v1:0": drop the vendor prefix,
  // the optional YYYYMMDD snapshot and the optional "-vN:M" version suffix.
  const match = modelId.match(
    /anthropic\.([a-z0-9-]+?)(?:-\d{8})?(?:-v\d+(?::\d+)?)?$/i
  );
  return match ? match[1] : modelId;
}
